/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Presentation vocabulary for the plan card.
 *
 * The card reads the v3 catalog shape: `features` is a record of toggles and
 * `limits` a record of quotas. This module maps those raw keys onto what the
 * card draws — which comparison row, which accent, which quota wording — and
 * invents nothing: every row is decided by the payload the API sends.
 *
 * Two things here are deliberately *relative* rather than absolute. The quota
 * rows print "4x mais" instead of "800 interações", and the price prints per
 * seat instead of per plan. Both are derived from the loaded catalogue, so a
 * republished `plans.json` moves them without a code change.
 */

import { resolvePlanTier, type PlanTier } from '@/shared/ui';
import type { PlanFeatureItem, PlanScopedLimits } from '@/features/pricing';

/** Single-path SVG glyphs, 24×24, drawn at 1.75 stroke. */
export const GLYPH = {
  chat: 'M7.9 20A9 9 0 1 0 4 16.1L2 22Z',
  search: 'M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z M21 21l-4.35-4.35',
  check: 'M20 6 9 17l-5-5',
  clock: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z M12 6v6l4 2',
} as const;

/**
 * Check and cross as the product already draws them: 20×20, filled, even-odd —
 * the same two paths used everywhere else a yes/no column appears.
 */
export const MARK = {
  yes: 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z',
  no: 'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z',
} as const;

export type TierKey = PlanTier;

/** Accent per tier — the gem's own colour, on the name and the quota values. */
export const TIER_ACCENT: Record<TierKey, string> = {
  emerald: 'var(--plan-tier-emerald)',
  ruby: 'var(--plan-tier-ruby)',
  diamond: 'var(--plan-tier-diamond)',
};

/** The tinted tile the gem glyph sits on, one gradient per tier. */
export const TIER_TILE: Record<TierKey, string> = {
  emerald: 'var(--plan-tier-emerald-tile)',
  ruby: 'var(--plan-tier-ruby-tile)',
  diamond: 'var(--plan-tier-diamond-tile)',
};

/**
 * Resolves a plan key to its gem tier. Delegates to the shared resolver so the
 * card, the tier icons and the rest of the app cannot drift apart on what
 * `essencial` means.
 *
 * @param planKey - The plan key derived from the slug.
 * @returns The gem tier, defaulting to emerald for unknown keys.
 */
export function resolveTier(planKey: string): TierKey {
  return resolvePlanTier(planKey);
}

/** One row of the comparison list: a name, and whether this plan has it. */
/** Where a comparison row reads its answer from. */
export type RowSource =
  /** Every listed feature key must be on for the row to read as a check. */
  | { from: 'features'; keys: string[] }
  /** A metered quota, printed relative to the entry plan. */
  | { from: 'quota'; key: QuotaKey }
  /** The storage allowance, printed as GB or TB. */
  | { from: 'storage' };

export interface FeatureRow {
  /** Row id; also the i18n suffix under `planChoice.matrix`. */
  id: string;
  /** Where the row's answer comes from. */
  source: RowSource;
}

/**
 * The comparison rows, in the order the card draws them: what every plan shares
 * first, then what separates the tiers. A row the plan does *not* have is still
 * drawn, with a cross — the whole point of the list is that the three cards can
 * be read across.
 *
 * `crm` bundles the eight keys that are standard CRM for an agency: clients and
 * their attendance, properties, launches, sales, leases, the calendar and
 * visits. "CRM completo" is one product promise, and splitting it into eight
 * rows that are on for every plan would separate nothing. `social_media` is
 * deliberately *not* in that bundle: publishing to social networks is not what
 * anyone means by a CRM, and folding it in would hide a feature inside a row
 * nobody reads for it.
 */
export const FEATURE_ROWS: FeatureRow[] = [
  {
    id: 'crm',
    source: {
      from: 'features',
      keys: [
        'client', 'client_attendance', 'properties', 'launches',
        'sale', 'lease', 'calendar', 'visits',
      ],
    },
  },
  { id: 'social_media', source: { from: 'features', keys: ['social_media'] } },
  { id: 'interactions', source: { from: 'quota', key: 'interactions' } },
  { id: 'partnership_searches', source: { from: 'quota', key: 'partnership_searches' } },
  { id: 'storage', source: { from: 'storage' } },
  { id: 'agency_site', source: { from: 'features', keys: ['agency_site'] } },
];

/** What a resolved row prints in its value column. */
export type RowValue =
  /** The plan has it, with nothing further to say. */
  | { kind: 'included' }
  /** The plan does not have it. */
  | { kind: 'absent' }
  /** A whole multiple of the entry plan's allowance. */
  | { kind: 'multiplier'; factor: number }
  /** No ceiling. */
  | { kind: 'unlimited' }
  /** An amount the row states outright, already formatted. */
  | { kind: 'amount'; text: string };

/** A comparison row already resolved against one plan. */
export interface ResolvedFeatureRow extends FeatureRow {
  on: boolean;
  value: RowValue;
}

/**
 * Collects the feature keys a plan actually has switched on.
 *
 * @param features - The plan's feature items, as built by `usePlans`.
 * @returns The set of enabled keys.
 */
export function enabledFeatures(features: PlanFeatureItem[] | undefined): Set<string> {
  const enabled = new Set<string>();
  (features ?? []).forEach((f) => {
    if (f.tier && f.tier !== 'none') enabled.add(f.key);
  });
  return enabled;
}

export interface MatrixFormatters {
  /** Formats a storage allowance already reduced to GB or TB. */
  storage: (gigabytes: number) => string;
}

/** Everything one row needs about the plan it is being resolved against. */
export interface MatrixSource {
  features: Set<string>;
  limits: PlanScopedLimits | undefined;
  quotas: Partial<Record<QuotaKey, QuotaLabel>>;
}

/**
 * Resolves one row against one plan.
 *
 * @param row - The row to resolve.
 * @param source - The plan's features, limits and quota ladder.
 * @param format - How to print storage and minutes.
 * @returns The row's mark and value.
 */
function resolveRow(
  row: FeatureRow, source: MatrixSource, format: MatrixFormatters,
): ResolvedFeatureRow {
  const value = rowValue(row.source, source, format);
  return { ...row, value, on: value.kind !== 'absent' };
}

/**
 * Reads one row's answer out of the payload.
 *
 * A feature group with some keys on and some off reads as off: the backend
 * matrix flips bundled keys together, so a partial group is a catalogue bug,
 * not a product state worth a third mark.
 *
 * @param source - Where the row reads from.
 * @param plan - The plan's features, limits and quota ladder.
 * @param format - How to print storage and minutes.
 * @returns What the value column prints.
 */
function rowValue(
  source: RowSource, plan: MatrixSource, format: MatrixFormatters,
): RowValue {
  if (source.from === 'features') {
    const on = source.keys.every((k) => plan.features.has(k));
    return on ? { kind: 'included' } : { kind: 'absent' };
  }
  if (source.from === 'quota') {
    return plan.quotas[source.key] ?? { kind: 'absent' };
  }
  // The drive is one pool the account shares, so it is read off the agency
  // block and printed as-is — never multiplied by the seat count.
  const gigabytes = plan.limits?.agency.storage_gb;
  if (typeof gigabytes !== 'number') return { kind: 'absent' };
  return { kind: 'amount', text: format.storage(gigabytes) };
}

/**
 * Resolves every comparison row against one plan.
 *
 * @param plan - The plan's features, limits and quota ladder.
 * @param format - How to print storage and minutes.
 * @returns Every row, in card order, each marked and valued.
 */
export function featureMatrix(
  plan: MatrixSource, format: MatrixFormatters,
): ResolvedFeatureRow[] {
  return FEATURE_ROWS.map((row) => resolveRow(row, plan, format));
}

/** The metered rows, in the order the card draws them. */
export const QUOTA_KEYS = ['interactions', 'partnership_searches'] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/** The glyph each metered row carries. */
export const QUOTA_ICON: Record<QuotaKey, string> = {
  interactions: GLYPH.chat,
  partnership_searches: GLYPH.search,
};

/**
 * How one quota reads on the card. Absolute numbers were dropped on purpose:
 * "800 interações" invites the customer to budget against a number they cannot
 * yet estimate, where "4x mais" answers the only question the card is being
 * asked — how much more than the plan next to it.
 */
export type QuotaLabel =
  | { kind: 'included' }
  | { kind: 'multiplier'; factor: number }
  | { kind: 'unlimited' };

/** Every plan's quota labels, keyed by plan key then quota key. */
export type QuotaLabels = Record<string, Partial<Record<QuotaKey, QuotaLabel>>>;

/** The minimum a plan has to report for the ladder to say anything. */
const MIN_BASELINE = 1;

/**
 * Finds the smallest reported value for one quota across the catalogue — the
 * entry-level plan, whichever tier that happens to be.
 *
 * @param plans - The plans on screen, with their limits.
 * @param key - The quota key.
 * @returns The baseline value, or null when nothing usable is reported.
 */
function baselineFor(plans: QuotaSource[], key: QuotaKey): number | null {
  const values = plans
    .map((p) => p.limits?.seat[key])
    .filter((v): v is number => typeof v === 'number' && v >= MIN_BASELINE);
  return values.length ? Math.min(...values) : null;
}

/** The slice of a plan the quota ladder needs. */
export interface QuotaSource {
  key: string;
  limits?: PlanScopedLimits;
}

/**
 * Builds the relative quota wording for every plan on screen.
 *
 * The entry-level plan reads "included"; the others read as a whole multiple of
 * it, rounded — the catalogue's 3/12/40 partner searches are 4x and 13.3x, and
 * "13x mais" is the honest reading of the second one. A plan reporting `null`
 * has no ceiling and reads as unlimited; a plan not reporting the key at all
 * gets no row.
 *
 * @param plans - The plans on screen.
 * @returns Labels keyed by plan key then quota key.
 */
export function buildQuotaLabels(plans: QuotaSource[]): QuotaLabels {
  const labels: QuotaLabels = {};
  plans.forEach((plan) => { labels[plan.key] = {}; });
  QUOTA_KEYS.forEach((key) => {
    const baseline = baselineFor(plans, key);
    plans.forEach((plan) => {
      const label = quotaLabelFor(plan.limits, key, baseline);
      if (label) labels[plan.key][key] = label;
    });
  });
  return labels;
}

/**
 * Places one plan's quota on the ladder.
 *
 * @param limits - The plan's scoped limits.
 * @param key - The quota key.
 * @param baseline - The catalogue's smallest reported value, or null.
 * @returns The label, or null when the plan does not report this quota.
 */
function quotaLabelFor(
  limits: PlanScopedLimits | undefined, key: QuotaKey, baseline: number | null,
): QuotaLabel | null {
  // The ladder only ever compares what one person draws down: comparing a
  // shared pool against a per-person allowance would put two different units
  // on the same scale.
  if (!limits || !(key in limits.seat)) return null;
  const value = limits.seat[key];
  if (value === null) return { kind: 'unlimited' };
  if (!baseline || value <= baseline) return { kind: 'included' };
  return { kind: 'multiplier', factor: Math.round(value / baseline) };
}

const GIGABYTES_PER_TERABYTE = 1024;

/**
 * Renders a storage quota as GB or TB.
 *
 * @param gigabytes - The quota in gigabytes.
 * @param format - Locale-aware number formatter.
 * @returns The printable amount, e.g. `100 GB` or `1 TB`.
 */
export function formatStorage(gigabytes: number, format: (n: number) => string): string {
  if (gigabytes >= GIGABYTES_PER_TERABYTE && gigabytes % GIGABYTES_PER_TERABYTE === 0) {
    return `${format(gigabytes / GIGABYTES_PER_TERABYTE)} TB`;
  }
  return `${format(gigabytes)} GB`;
}

/** One plan's line in the storage footnote. */
export interface StorageNoteEntry {
  planKey: string;
  title: string;
  gigabytes: number;
}

/** The slice of a plan the storage footnote needs. */
export interface StorageSource extends QuotaSource {
  title: string;
}

/**
 * Collects the storage each plan includes, for the footnote under the grid.
 *
 * Storage is real but it is not what anyone chooses a plan on, so it is stated
 * once below the cards instead of taking a row inside all three.
 *
 * @param plans - The plans on screen.
 * @returns One entry per plan that reports a storage quota, in card order.
 */
export function storageNote(plans: StorageSource[]): StorageNoteEntry[] {
  return plans
    .map((p) => ({ planKey: p.key, title: p.title, gigabytes: p.limits?.agency.storage_gb }))
    .filter((e): e is StorageNoteEntry => typeof e.gigabytes === 'number');
}

/** Seats a plan carries when the catalogue does not say. */
const DEFAULT_SEATS = 1;

/**
 * What one seat of a plan costs per month.
 *
 * Two catalogue shapes are in circulation and the card has to read both. The
 * per-seat catalogue states the seat price outright, so the price is taken as
 * it comes. The older package catalogue priced a plan whole, with 3, 12 or 40
 * seats bundled into totals of 246, 496 and 1246 that are not comparable
 * across the three cards; there, dividing by the bundle is what recovers a
 * seat. Telling them apart by the field rather than by a date keeps the card
 * correct on both sides of the catalogue migration — assuming either one
 * misprices the other by a factor of the seat ceiling.
 *
 * @param price - The plan's monthly price, for the cycle already resolved.
 * @param seats - The seats the plan bundles, when it bundles any.
 * @param pricesBySeat - Whether the catalogue states a seat price of its own.
 * @returns The per-seat monthly price.
 */
export function perSeatPrice(
  price: number,
  seats: number | undefined,
  pricesBySeat = false,
): number {
  if (pricesBySeat) return price;
  return price / Math.max(seats ?? DEFAULT_SEATS, DEFAULT_SEATS);
}

/**
 * Replaces the dash the API uses to glue the audience to its qualifier. Dropping
 * it outright leaves an ungrammatical run-on, and left in place it dangles at a
 * line break; a comma reads as one sentence at any wrap point.
 *
 * @param text - The raw description.
 * @returns The sentence with the separator dash turned into a comma.
 */
export function stripSeparatorDash(text: string): string {
  return text.replace(/\s+[—–-]\s+/g, ', ').trim();
}

/** What the agency card's one-line summary says about a plan. */
export interface AgencyRowFacts {
  /** The interaction quota's place on the ladder, when above the baseline. */
  factor: number | null;
  /** The shared drive in gigabytes, when the plan reports one. */
  storageGb: number | null;
  /** The one capability worth naming beside the numbers. */
  highlight: 'advanced_agents' | 'custom_domain' | null;
}

/** The slice of a plan the agency row summary reads. */
export interface AgencyRowSource {
  features?: PlanFeatureItem[];
  limits?: PlanScopedLimits;
}

/**
 * Reads the three facts that distinguish one agency plan from the one below it.
 *
 * The copy is composed from these rather than written out, because a sentence
 * saying "4x mais IA, 100 GB e domínio próprio" restates three numbers the
 * catalogue already publishes. Written down, it survives the catalogue
 * changing — which is the whole failure this screen keeps having.
 *
 * @param plan - The plan's features and scoped limits.
 * @param quota - The plan's quota labels, as built by `buildQuotaLabels`.
 * @returns The facts the summary interpolates.
 */
export function agencyRowFacts(
  plan: AgencyRowSource,
  quota: Partial<Record<QuotaKey, QuotaLabel>> | undefined,
): AgencyRowFacts {
  const interactions = quota?.interactions;
  return {
    factor: interactions?.kind === 'multiplier' ? interactions.factor : null,
    storageGb: plan.limits?.agency.storage_gb ?? null,
    // Nothing distinguishes the tiers by capability any more: the custom
    // domain and the three advanced agents left the catalogue, and every
    // remaining feature is on everywhere. What a row can still highlight is
    // volume, which the factor and the storage already carry.
    highlight: null,
  };
}
